import { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { AppSettings } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useRegion } from 'components/app-shell/context/RegionContext'
import { SettingsTabs } from 'components/SettingsTabs'
import { Button, Card, ErrorState, SelectField, Spinner } from 'components/ui'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const CURRENCIES = [
  { ar: 'JOD — دينار أردني', code: 'JOD', en: 'JOD — Jordanian dinar' },
  { ar: 'AED — درهم إماراتي', code: 'AED', en: 'AED — UAE dirham' },
  { ar: 'SAR — ريال سعودي', code: 'SAR', en: 'SAR — Saudi riyal' },
  { ar: 'QAR — ريال قطري', code: 'QAR', en: 'QAR — Qatari riyal' },
  { ar: 'KWD — دينار كويتي', code: 'KWD', en: 'KWD — Kuwaiti dinar' },
  { ar: 'BHD — دينار بحريني', code: 'BHD', en: 'BHD — Bahraini dinar' },
  { ar: 'OMR — ريال عُماني', code: 'OMR', en: 'OMR — Omani rial' },
]

/** Organisation language and region (Figma: Admin / 31 · Settings — Language & region).
 *  Language here is the default for the interface, sign-in, and print preview. People override it from Profile.
 *  The Excel template stays the approved Arabic workbook. */
export const LanguageSettingsPage = () => {
  const { user } = useAuth()
  const { adoptSettings } = useRegion()
  const { data: settings, error, loading, reload } = useAsync(api.fetchAppSettings, [])
  const { busy, run } = useAction()
  const [form, setForm] = useState<AppSettings | null>(null)

  useEffect(() => {
    if (settings && !form) setForm(settings)
  }, [settings, form])

  if (!user?.isAdmin) return <ForbiddenPage message={t('اللغة والمنطقة متاحتان لمدير النظام فقط.', 'Language and region are available to Admin only.')} />

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!form) return
    const saved = await run('language', () => api.updateAppSettings(form), t('تم حفظ اللغة والمنطقة', 'Language and region saved'))
    if (saved) {
      adoptSettings(saved)
      reload()
    }
  }

  const organisationName = settings?.organisationName ?? 'JoPath'

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الإعدادات', 'Settings')}</h1>
          <p className="muted">{t(`${organisationName} · التغييرات تُطبّق على كل مشروع`, `${organisationName} · Changes apply to every project`)}</p>
        </div>
      </div>

      <SettingsTabs />

      {loading ? (
        <Spinner />
      ) : error || !form ? (
        <ErrorState message={error ?? t('تعذر تحميل الإعدادات', 'Could not load settings')} onRetry={reload} />
      ) : (
        <form className="stack" onSubmit={save}>
          <Card title={t('اللغة', 'Language')}>
            <p className="muted small">
              {t(
                'الافتراضي للمستخدمين الجدد. يغيّر لغة الواجهة وشاشة الدخول، ومعاينة الطباعة تتبع لغة التصدير.',
                'Default for new users. It changes the interface and the sign-in screen. Print preview follows the export language.'
              )}
            </p>
            <div className="form-grid">
              <SelectField
                label={t('اللغة الافتراضية', 'Default language')}
                hint={t('يمكن لكل شخص تغيير لغته من الملف الشخصي', 'Each person can change their language in Profile. It is saved in this browser.')}
                value={form.defaultLocale}
                onChange={(event) => setForm({ ...form, defaultLocale: event.target.value as AppSettings['defaultLocale'] })}
                required
              >
                <option value="ar">{t('العربية — Arabic', 'Arabic')}</option>
                <option value="en">{t('English — الإنجليزية', 'English')}</option>
              </SelectField>
              <SelectField
                label={t('لغة التصدير', 'Export language')}
                hint={t('قالب إكسل المعتمد يبقى بالعربية. معاينة الطباعة تتبع هذا الاختيار.', 'The Excel workbook stays Arabic. Print preview follows the export language.')}
                value={form.exportLocale}
                onChange={(event) => setForm({ ...form, exportLocale: event.target.value as AppSettings['exportLocale'] })}
              >
                <option value="reader">{t('نفس لغة القارئ', 'Same as the reader')}</option>
                <option value="ar">{t('العربية دائماً', 'Always Arabic')}</option>
                <option value="en">{t('الإنجليزية دائماً', 'Always English')}</option>
              </SelectField>
            </div>
          </Card>

          <Card title={t('المنطقة والصيغ', 'Region and formats')}>
            <div className="form-grid">
              <SelectField
                label={t('صيغة التاريخ', 'Date format')}
                value={form.dateFormat}
                onChange={(event) => setForm({ ...form, dateFormat: event.target.value as AppSettings['dateFormat'] })}
              >
                <option value="DMY">{t('13/10/2026 (يوم/شهر/سنة)', '13/10/2026 (Day/Month/Year)')}</option>
                <option value="MDY">{t('10/13/2026 (شهر/يوم/سنة)', '10/13/2026 (Month/Day/Year)')}</option>
                <option value="ISO">2026-10-13</option>
              </SelectField>
              <SelectField
                label={t('صيغة الوقت', 'Time format')}
                value={form.timeFormat}
                onChange={(event) => setForm({ ...form, timeFormat: event.target.value as AppSettings['timeFormat'] })}
              >
                <option value="24">{t('24 ساعة (17:30)', '24-hour (17:30)')}</option>
                <option value="12">{t('12 ساعة (5:30 م)', '12-hour (5:30 PM)')}</option>
              </SelectField>
              <SelectField label={t('الوحدات', 'Units')} value={form.units} onChange={() => undefined}>
                <option value="metric">{t('مترية (م، م²، م³، كغ، °م)', 'Metric (m, m², m³, kg, °C)')}</option>
              </SelectField>
              <SelectField
                label={t('العملة', 'Currency')}
                value={form.currency}
                onChange={(event) => setForm({ ...form, currency: event.target.value })}
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {t(currency.ar, currency.en)}
                  </option>
                ))}
              </SelectField>
            </div>
            <p className="muted small">{t('الوحدات الإمبراطورية غير مدعومة بعد.', 'Imperial units are not supported.')}</p>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.latinDigits}
                onChange={(event) => setForm({ ...form, latinDigits: event.target.checked })}
              />
              {t('أرقام لاتينية داخل العربية (0–9)', 'Latin digits in Arabic (0–9)')}
            </label>
            <p className="muted small">
              {t('إظهار 0–9 بدل الأرقام العربية الهندية حتى تطابق الكميات المخططات وملفات إكسل.', 'Show 0–9 instead of Arabic-Indic digits so quantities match drawings and Excel files.')}
            </p>
            <div className="form-actions">
              <Button type="submit" variant="primary" busy={busy === 'language'}>
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </Card>
        </form>
      )}

      {user?.isAdmin ? (
        <p className="muted small">
          {t('لغتك أنت تُضبط من ', 'Your language is set in ')}
          <Link to="/profile">{t('الملف الشخصي', 'Profile')}</Link>
          {t('، وهذا الافتراضي لبقية المستخدمين.', ' and saved in this browser. This is the default for everyone else.')}
        </p>
      ) : null}
    </div>
  )
}
